import { convertKeyToTab, generateKeyFromTab, hasItem } from '@sravni/ugc/lib/specialOffers/helpers';
import { VITRINS_OFFERS_LIST } from '@sravni/ugc/lib/specialOffers/lists';
import type { Tab, VITRINS_OFFERS_TYPE } from '@sravni/ugc/lib/specialOffers/types';

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import type { OffersByType } from '../../services/offers';
import { getOffersByLocationAndPutToCache } from '../../services/offers';
import type { GlobalState } from '../index';
import { getAreOffersLoadingByType, getOffersHaveBeenLoadedByType } from '../selectors/offers';

export interface IAnalyticsParams {
  utmParams: Record<string, string>;
  testIdVariant: string;
}

export interface IState {
  offers: {
    [type in VITRINS_OFFERS_TYPE]: {
      list: any[];
      loaded: boolean;
      isLoading: boolean;
      attempt: number;
    };
  };
  analyticsParams: IAnalyticsParams;
  isAnalyticsParamsLoaded: boolean;
  activeTabType: Tab;
  availableTabs: Tab[];
  pixels: string[];
  isRequestForOrganizationDone: boolean;
}

const initialState: IState = {
  offers: null,
  analyticsParams: {
    utmParams: {},
    testIdVariant: '',
  },
  isAnalyticsParamsLoaded: false,
  activeTabType: null,
  availableTabs: [],
  pixels: [],
  isRequestForOrganizationDone: false,
};

interface FetchOffersParams {
  location: string;
  offerTypes: Tab<VITRINS_OFFERS_TYPE>[];
  bonusFilter?: string[];
  organizationId?: string[];
}

const fetchOffers = createAsyncThunk<
  OffersByType,
  FetchOffersParams,
  {
    state: GlobalState;
  }
>('offers/fetchOffers', async (params) => {
  const offers = await getOffersByLocationAndPutToCache(params);

  return offers;
});

interface CheckAndFetchOffersParams {
  location: string;
  offerTypes: Tab[];
  bonusFilter?: string[];
  organizationId?: string[];
}

export const checkAndFetchOffers = createAsyncThunk<
  void,
  CheckAndFetchOffersParams,
  {
    state: GlobalState;
  }
>('offers/checkAndFetchOffers', (params, { getState, dispatch }) => {
  const state = getState();
  const missingTypes: Tab<VITRINS_OFFERS_TYPE>[] = [];

  params.offerTypes.forEach((tab) => {
    if (!hasItem(VITRINS_OFFERS_LIST, tab)) return;

    const wereOffersLoaded = getOffersHaveBeenLoadedByType(tab as Tab<VITRINS_OFFERS_TYPE>)(state);
    const isTypeLoading = getAreOffersLoadingByType(tab as Tab<VITRINS_OFFERS_TYPE>)(state);

    const attempt = state.offers.offers?.[generateKeyFromTab(tab)]?.attempt ?? 0;

    if (!wereOffersLoaded && !isTypeLoading && attempt < 2) {
      missingTypes.push(tab as Tab<VITRINS_OFFERS_TYPE>);
    }
  });

  if (missingTypes.length) {
    dispatch(
      fetchOffers({
        ...params,
        offerTypes: missingTypes,
      }),
    );
  }
});

const offersSlice = createSlice({
  name: 'offers',
  initialState,
  reducers: {
    setAnalyticsParams: (state, { payload }) => {
      state.analyticsParams = payload;
      state.isAnalyticsParamsLoaded = true;
    },
    setActiveTabType: (state, { payload }) => {
      state.activeTabType = payload;
    },
    addPixel: (state, { payload }) => {
      state.pixels.push(payload);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchOffers.fulfilled, (state, { payload, meta }) => {
      Object.entries(payload).forEach(([typeKey, offers]) => {
        const offersCount = offers.length;

        state.offers = {
          ...state.offers,
          [typeKey]: {
            ...state.offers?.[typeKey],
            ...(offersCount && { list: offers }),
            ...(offersCount && { loaded: true }),
            isLoading: false,
          },
        };

        if (offersCount) {
          state.availableTabs = [...state.availableTabs, convertKeyToTab(typeKey)];
        }
      });

      meta.arg.offerTypes.forEach((type) => {
        const typeKey = generateKeyFromTab(type);

        state.offers = {
          ...state.offers,
          [typeKey]: {
            ...state.offers?.[typeKey],
            isLoading: false,
          },
        };
      });

      state.isRequestForOrganizationDone = !!meta.arg.organizationId;
    });
    builder.addCase(fetchOffers.pending, (state, { meta }) => {
      meta.arg.offerTypes.forEach((type) => {
        const typeKey = generateKeyFromTab(type);

        state.offers = {
          ...state.offers,
          [typeKey]: {
            ...state.offers?.[typeKey],
            isLoading: true,
            attempt: (state.offers?.[typeKey]?.attempt ?? 0) + 1,
          },
        };
      });
    });
    builder.addCase(fetchOffers.rejected, (state, { meta }) => {
      meta.arg.offerTypes.forEach((type) => {
        const typeKey = generateKeyFromTab(type);

        state.offers = {
          ...state.offers,
          [typeKey]: {
            ...state.offers?.[typeKey],
            isLoading: false,
          },
        };
      });
    });
  },
});

const { reducer } = offersSlice;

export const { setAnalyticsParams, setActiveTabType, addPixel } = offersSlice.actions;
export default reducer;
